import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import styled, { createGlobalStyle } from 'styled-components';
import { Route, Switch, Redirect } from 'react-router';

import { updateBuckUser } from 'features/buckaneer/actions';
import { getBuckUserCart } from 'features/cart/actions';
import config from 'init/config';

import loadable from '@loadable/component';

import PrivateRoute from 'utilities/privateRoute';
import PrerenderSafeLazyLoad from 'components/PrerenderSafeLazyLoad';
import ErrorBoundary from 'components/ErrorBoundary';

const Header = loadable(() => import('views/Layout/Header'));
const Footer = loadable(() => import('views/Layout/Footer'));
const Login = loadable(() => import(`views/Login`));

const Main = styled.div`
  padding-top: 64px;
  flex: 1 0 auto;
  min-height: calc(100vh - 16px);
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const GlobalStyle = createGlobalStyle`
  body, html, #root {
    height:100%;
  }
  body {
    font-family:'runda', sans-serif;

    > img {
      display:none; /*Tracking pixels breaking flex layout*/
    }
  }
  //Hide Hubspot chat on mobile
  @media (max-width: 467px) {
    #hubspot-messages-iframe-container, #hubspot-messages-iframe-container > iframe {
      display:none;
      visibility: collapse;
    }
  }
  //Adjust Contentstack Editable Tags tooltips
  button#cslp-tooltip {
    z-index: 9999!important;
    margin-top: 5px;
  }

`;

const MarketingBase = ({ user, updateBuckUser, getBuckUserCart }) => {
  useEffect(() => {
    updateBuckUser();
    try {
      getBuckUserCart();
    } catch (e) {
      console.error(e);
    }
  }, [updateBuckUser, getBuckUserCart]);

  const { email, first_name, last_name, company_name } = user;

  if (email && email !== '') {
    // If the user is logged in, identify them to Heap
    if (window.heap) {
      window.heap.identify(email);
      window.heap.addUserProperties({
        Email: email,
        CompanyName: company_name,
        CustomerName: `${first_name} ${last_name}`
      });
    }
  }

  return (
    <Wrapper>
      <GlobalStyle />
      <Header />
      <Main data-swiftype-index="true">
        <ErrorBoundary>
          <Switch>
            <Route
              path="/search"
              component={loadable(() => import(`views/Search`))}
            />
            <Route path="/login" component={Login} />
            <Route path="/logout" component={Login} />
            <Route path="/register" component={Login} />
            <Route path="/reset-password" component={Login} />
            <Route path="/send-password-reset" component={Login} />
            <Route path="/verify-email" component={Login} />
            <Route path="/verify-email-error" component={Login} />
            <PrivateRoute
              path="/id/"
              component={loadable(() => import(`views/Layout/IdBase`))}
            />
            <Route
              path="/checkout/info/"
              component={() => {
                window.location = `${config.buckRootUrl}/checkout/info/`;
                return null;
              }}
            />

            <Route
              path="/guided-edit/flow"
              component={loadable(() => import('views/GuidedEdit/Flow'))}
            />
            <Route
              path="/guided-edit/:destinationId?"
              component={loadable(() => import('views/GuidedEdit'))}
            />
            <Route
              path="/products/:family/pricing"
              component={loadable(() => import(`views/Product/Pricing`))}
              exact
            />
            <Route
              path="/products/:family/:name/:section?"
              component={loadable(() => import(`views/Product/Detail`))}
              exact
            />
            <Route
              path="/products/:family"
              component={loadable(() => import(`views/Product/Family`))}
              exact
            />
            <Route path="/request/engineered-cells/advanced-cells" exact>
              <Redirect to="/quote" />
            </Route>

            <Route path="/request/crispr-kits/advanced-rna" exact>
              <Redirect to="/quote" />
            </Route>

            <Route path="/request/gmp-rna" exact>
              <Redirect to="/quote" />
            </Route>

            <PrivateRoute
              path="/request/:requestSlug"
              component={loadable(() => import(`views/Order/Advanced`))}
              exact
            />
            <Route
              path="/order/crispr"
              component={loadable(() =>
                import('views/Order/sgRNAUnified/Unified')
              )}
              exact
            />
            <Route
              path="/order/crispr/edit/:orderToken"
              component={loadable(() =>
                import('views/Order/sgRNAUnified/Unified')
              )}
              exact
            />
            <Route
              path="/order/summary/:orderToken"
              component={loadable(() => import('views/Order/Summary'))}
              exact
            />
            <Route
              path="/order/crispr-kits/custom-libraries"
              component={loadable(() => import('views/Order/ODL'))}
              exact
            />
            <Route
              path="/order/crispr-kits/gene-knockout-kit"
              component={loadable(() => import('views/Order/GKO'))}
              exact
            />
            <Route
              path="/order/crispr-kits/synthetic-sgrna"
              component={loadable(() => import('views/Order/sgRNA/Standard'))}
              exact
            />
            <Route
              path="/order/crispr-kits/synthetic-sgrna/edit/:orderToken"
              component={loadable(() => import('views/Order/sgRNA/Standard'))}
              exact
            />

            <Route
              path="/order/crispr-kits/advanced-rna"
              component={loadable(() => import('views/Order/sgRNA/Advanced'))}
              exact
            />
            <Route
              path="/order/crispr-kits/advanced-rna/edit/:orderToken"
              component={loadable(() => import('views/Order/sgRNA/Advanced'))}
              exact
            />
            <Route
              path="/order/engineered-cells/custom-libraries"
              component={loadable(() => import('views/Order/ECL'))}
              exact
            />
            <Route
              path="/order/engineered-cells/express-knockout-cells"
              component={loadable(() => import('views/Order/XKO'))}
              exact
            />
            <Route
              path="/order/add-ons"
              component={loadable(() => import('views/Order/AddOns'))}
              exact
            />
            <Route
              path="/order/:familySlug/:productSlug"
              component={loadable(() => import('views/Order'))}
              exact
            />
            <Route
              path="/blog/"
              component={loadable(() => import('views/Blog'))}
            />
            <Route
              path="/events/thanks"
              component={loadable(() => import('views/Events/Thanks'))}
              exact
            />
            <Route
              path="/events/:eventSlug"
              component={loadable(() => import('views/Events/Detail'))}
              exact
            />
            <Route
              path="/events"
              component={loadable(() => import('views/Events'))}
              exact
            />
            <Route
              path="/publications/:publicationsSlug"
              component={loadable(() => import('views/Publications/Detail'))}
              exact
            />
            <Route
              path="/publications"
              component={loadable(() => import('views/Publications'))}
              exact
            />
            <Route
              path="/success/:offerSlug"
              component={loadable(() => import('views/Offers/Detail'))}
              exact
            />
            <Route
              path="/success"
              component={loadable(() => import('views/Offers'))}
              exact
            />
            <Route
              path="/crispr-cuts/"
              component={loadable(() => import('views/Podcast'))}
            />
            <Route
              path="/careers"
              component={loadable(() => import('views/Company/Careers'))}
              exact
            />
            <Route
              path="/team"
              component={loadable(() => import('views/Company/Team'))}
              exact
            />
            <Route
              path="/team/:teamSlug"
              component={loadable(() => import('views/Company/Team/Detail'))}
              exact
            />
            <Route
              path="/cart"
              component={loadable(() => import('views/Cart'))}
              exact
            />
            <Route
              path="/customers"
              component={loadable(() => import('views/Customers'))}
              exact
            />
            <Route
              path="/success/:offerSlug"
              component={loadable(() => import('views/Offers/Detail'))}
              exact
            />
            <Route
              path="/cart/:orderToken?"
              component={loadable(() => import('views/Cart'))}
              exact
            />
            <PrivateRoute
              path="/checkout/:orderToken"
              component={loadable(() => import('views/Checkout'))}
              exact
            />
            <Route
              path="/customers"
              component={loadable(() => import('views/Customers'))}
              exact
            />
            <Route
              path="/applications/:applicationsSlug"
              component={loadable(() => import('views/Applications/Detail'))}
              exact
            />
            <Route
              path="/applications"
              component={loadable(() => import('views/Applications'))}
              exact
            />
            <Route
              path="/contact/:contactSlug"
              component={loadable(() => import('views/Contact/Detail'))}
              exact
            />
            <Route
              path="/contact"
              component={loadable(() => import('views/Contact'))}
              exact
            />
            <Route
              path="/company"
              component={loadable(() => import('views/Company'))}
              exact
            />
            <Route
              path="/resources/all/:resourceFilter?"
              component={loadable(() => import('views/Resources'))}
              exact
            />
            <Route
              path="/resources/:resourceSlug"
              component={loadable(() => import('views/Resources/Detail'))}
              exact
            />
            <Route
              path="/resources"
              component={loadable(() => import('views/Resources'))}
            />
            <Route
              path="/workflows/:workflowSlug"
              component={loadable(() => import('views/Workflows/Detail'))}
              exact
            />
            <Route
              path="/platforms/:platformSlug?/:capabilitySlug?"
              component={loadable(() => import('views/Platforms/Detail'))}
              exact
            />
            <Route
              path="/help/:articleSlug"
              component={loadable(() => import('views/Help/Detail'))}
              exact
            />
            <Route
              path="/help/category/:categorySlug"
              component={loadable(() => import('views/Help/Category'))}
              exact
            />
            <Route
              path="/help"
              component={loadable(() => import('views/Help'))}
              exact
            />
            <Route
              path="/legal"
              component={loadable(() => import('views/Legal'))}
            />
            <Route
              path="/press/:pressSlug"
              component={loadable(() => import('views/Press/Detail'))}
              exact
            />
            <Route
              path="/press"
              component={loadable(() => import('views/Press'))}
              exact
            />
            <Route
              path="/thanks/:thankyouSlug"
              component={loadable(() => import('views/ThankYou'))}
              exact
            />
            <Route
              path="/learn/:hubSlug"
              component={loadable(() => import('views/Hub'))}
              exact
            />
            <Route
              path="/survey/:surveySlug"
              component={loadable(() => import('views/Survey'))}
              exact
            />
            <Route
              path="/guide/:guideName/:chapter"
              component={loadable(() => import('views/Guide/Chapter'))}
              exact
            />
            <Route
              path="/guide/:guideName"
              component={loadable(() => import('views/Guide/Home'))}
              exact
            />
            <Route
              path="/calculator/:formatSlug?/:methodSlug?"
              component={loadable(() => import('views/Calculator'))}
            />
            <Route
              path="/insights/:insightsSlug"
              component={loadable(() => import('views/Experiences/Detail'))}
            />
            <Route
              path="/stories/:caseStudySlug"
              component={loadable(() => import('views/CaseStudies/Detail'))}
              exact
            />
            <Route
              path="/stories"
              component={loadable(() => import('views/CaseStudies'))}
              exact
            />
            <Route
              path="/full-stack"
              component={loadable(() => import('views/Welcome'))}
              exact
            />
            <Route
              path="/:landingSlug"
              component={loadable(() => import('views/Landing'))}
            />
            <Route
              path="/"
              component={loadable(() => import('views/Home'))}
              exact
            />
          </Switch>
        </ErrorBoundary>
      </Main>
      <PrerenderSafeLazyLoad offset={100}>
        <Footer />
      </PrerenderSafeLazyLoad>
    </Wrapper>
  );
};

export default connect(state => ({ user: state.buckaneer.user }), {
  updateBuckUser,
  getBuckUserCart
})(MarketingBase);
